import React from 'react';
import { X, Calendar, Users, FolderOpen, FileText, PoundSterling, Building2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import type { TimesheetReportFilters } from '@/types/timesheetReport';
import { format } from 'date-fns';

interface ActiveFilterChipsProps {
  filters: TimesheetReportFilters;
  onRemoveFilter: (filterKey: string, value?: string | number) => void;
  onClearAll: () => void;
  availableEmployees: Array<{ id: number; name: string; department: string }>;
  availableProjects: Array<{ id: string; code: string; name: string }>;
  availableDepartments: string[];
}

interface FilterChip {
  id: string;
  icon: React.ReactNode;
  label: string;
  filterKey: string;
  value?: string | number;
}

export const ActiveFilterChips: React.FC<ActiveFilterChipsProps> = ({
  filters,
  onRemoveFilter,
  onClearAll,
  availableEmployees,
  availableProjects,
  availableDepartments,
}) => {
  const getActiveFilters = (): FilterChip[] => {
    const chips: FilterChip[] = [];

    // Date range
    if (filters.dateFrom || filters.dateTo) {
      let label = '';
      if (filters.quickDateRange) {
        // Use friendly name for quick date ranges
        switch (filters.quickDateRange) {
          case 'this-week':
            label = 'This Week';
            break;
          case 'last-week':
            label = 'Last Week';
            break;
          case 'this-month':
            label = 'This Month';
            break;
          case 'last-month':
            label = 'Last Month';
            break;
          default:
            label = 'Custom Range';
        }
      } else if (filters.dateFrom && filters.dateTo) {
        label = `${format(filters.dateFrom, 'MMM d')} - ${format(filters.dateTo, 'MMM d, yyyy')}`;
      } else if (filters.dateFrom) {
        label = `From ${format(filters.dateFrom, 'MMM d, yyyy')}`;
      } else if (filters.dateTo) {
        label = `Until ${format(filters.dateTo, 'MMM d, yyyy')}`;
      }
      
      chips.push({
        id: 'dateRange',
        icon: <Calendar className="h-3 w-3" />,
        label,
        filterKey: 'dateRange',
      });
    }

    // Statuses
    if (filters.statuses && filters.statuses.length > 0) {
      filters.statuses.forEach(status => {
        const statusLabels: Record<string, string> = {
          draft: 'Draft',
          submitted: 'Submitted',
          approved: 'Approved',
          rejected: 'Rejected',
          locked: 'Locked',
        };
        
        chips.push({
          id: `status-${status}`,
          icon: <FileText className="h-3 w-3" />,
          label: statusLabels[status] || status,
          filterKey: 'statuses',
          value: status,
        });
      });
    }

    // Employees
    if (filters.employeeIds && filters.employeeIds.length > 0) {
      filters.employeeIds.forEach(empId => {
        const employee = availableEmployees.find(e => e.id === empId);
        if (employee) {
          chips.push({
            id: `employee-${empId}`,
            icon: <Users className="h-3 w-3" />,
            label: employee.name,
            filterKey: 'employeeIds',
            value: empId,
          });
        }
      });
    }

    // Departments
    if (filters.departments && filters.departments.length > 0) {
      filters.departments.forEach(dept => {
        chips.push({
          id: `department-${dept}`,
          icon: <Building2 className="h-3 w-3" />,
          label: dept,
          filterKey: 'departments',
          value: dept,
        });
      });
    }

    // Projects
    if (filters.projectIds && filters.projectIds.length > 0) {
      filters.projectIds.forEach(projId => {
        const project = availableProjects.find(p => p.code === projId || p.id === projId);
        if (project) {
          chips.push({
            id: `project-${projId}`,
            icon: <FolderOpen className="h-3 w-3" />,
            label: `${project.code} - ${project.name}`,
            filterKey: 'projectIds',
            value: projId,
          });
        }
      });
    }

    // Billable only
    if (filters.billableOnly) {
      chips.push({
        id: 'billableOnly',
        icon: <PoundSterling className="h-3 w-3" />,
        label: 'Billable Only',
        filterKey: 'billableOnly',
      });
    }

    // Rejected only
    if (filters.rejectedOnly) {
      chips.push({
        id: 'rejectedOnly',
        icon: <FileText className="h-3 w-3" />,
        label: 'With Rejections',
        filterKey: 'rejectedOnly',
      });
    }

    return chips;
  };

  const activeFilters = getActiveFilters();

  if (activeFilters.length === 0) {
    return null;
  }

  return (
    <div className="flex items-center gap-2 mb-4 flex-wrap">
      <span className="text-sm text-gray-600 font-medium">Active Filters:</span>
      
      {activeFilters.map(filter => (
        <Badge
          key={filter.id}
          variant="secondary"
          className="gap-1.5 pr-1 pl-2 py-1 text-xs font-normal"
        >
          {filter.icon}
          <span>{filter.label}</span>
          <button
            onClick={() => onRemoveFilter(filter.filterKey, filter.value)}
            className="hover:text-destructive ml-1 px-0.5 rounded hover:bg-destructive/10 transition-colors"
            aria-label={`Remove ${filter.label} filter`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}
      
      <Button
        variant="ghost"
        size="sm"
        onClick={onClearAll}
        className="h-7 px-2 text-xs"
      >
        Clear All
      </Button>
    </div>
  );
};
